import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'

interface LightboxProps {
    isOpen: boolean
    onClose: () => void
    src: string | null
    alt?: string
    linkTo?: string
    linkState?: any
    info?: {
        title: string
        author: string
        date: string
    }
}

export function Lightbox({ isOpen, onClose, src, alt, linkTo, linkState, info }: LightboxProps) {
    // Lock body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen])

    // Close on escape key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', handleEsc)
        return () => window.removeEventListener('keydown', handleEsc)
    }, [onClose])

    if (!isOpen || !src) return null

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
                onClick={onClose}
            >
                {/* Controls */}
                <div className="absolute top-4 right-4 flex items-center gap-4 z-20">
                    <button
                        onClick={onClose}
                        className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                    >
                        <X className="w-8 h-8" />
                    </button>
                </div>

                {/* Image */}
                <motion.img
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    src={src}
                    alt={alt || 'Görsel'}
                    className="max-h-[calc(100vh-120px)] max-w-full object-contain rounded-lg shadow-2xl"
                    onClick={(e) => e.stopPropagation()} // Prevent closing when clicking image
                />

                {/* Info Bar */}
                {info && (
                    <div className="absolute bottom-0 left-0 right-0 bg-surface-900/90 backdrop-blur-md border-t border-white/5 p-4 z-20" onClick={(e) => e.stopPropagation()}>
                        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex flex-col gap-1 text-center md:text-left">
                                <h3 className="text-white font-poppins font-medium text-lg">{info.title}</h3>
                                <div className="flex items-center justify-center md:justify-start gap-2 text-sm text-gray-400">
                                    <span>{info.author}</span>
                                    <span>•</span>
                                    <span>{info.date}</span>
                                </div>
                            </div>

                            {linkTo && (
                                <Link
                                    to={linkTo}
                                    state={linkState}
                                    target="_blank"
                                    className="px-6 py-2 bg-surface-800 hover:bg-surface-700 text-gray-200 rounded-xl transition-all text-sm font-bold font-poppins border border-white/5 hover:border-white/10 shadow-sm whitespace-nowrap"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (linkState?.event) {
                                            try {
                                                localStorage.setItem(`g_evt_${linkState.event.id}`, JSON.stringify(linkState.event))
                                            } catch (err) {
                                                console.error('Failed to cache event', err)
                                            }
                                        }
                                    }}
                                >
                                    Detayları Gör
                                </Link>
                            )}
                        </div>
                    </div>
                )}
            </motion.div>
        </AnimatePresence>
    )
}
